import { useState } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  LayoutDashboard,
  Users,
  Truck,
  Package,
  FileText,
  ShoppingCart,
  BarChart3,
  Settings,
  LogOut,
  Menu,
  X,
  Bell,
  Search,
  ChevronDown,
  Receipt,
  PackagePlus,
  UserCheck,
  Calculator,
  Upload,
  FileCode,
  Boxes,
  DollarSign,
  RotateCcw
} from 'lucide-react';
import { Button } from './ui/button';
import { GoalCelebration } from './GoalCelebration';
import { ErrorBoundary } from './ErrorBoundary';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

const navigation = [
  { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
  { name: 'PDV', href: '/pdv', icon: Receipt, adminOnly: true },
  { name: 'Fechamento', href: '/fechamento', icon: Calculator, adminOnly: true },
  { name: 'Clientes', href: '/clientes', icon: Users },
  { name: 'Fornecedores', href: '/fornecedores', icon: Truck, adminOnly: true },
  { name: 'Produtos', href: '/produtos', icon: Package },
  { name: 'Entradas', href: '/entradas', icon: PackagePlus, adminOnly: true },
  { name: 'Pedidos', href: '/pedidos', icon: FileText },
  { name: 'Vendas', href: '/vendas', icon: ShoppingCart },
  { name: 'Devoluções', href: '/devolucoes', icon: RotateCcw },
  { name: 'Vendedores', href: '/vendedores', icon: UserCheck },
  { name: 'Comissões', href: '/comissoes', icon: DollarSign },
  { name: 'Controle Estoque', href: '/estoque', icon: Boxes },
  { name: 'Importar Dados', href: '/importar', icon: Upload, adminOnly: true },
  { name: 'NFC-e', href: '/nfce', icon: FileCode, adminOnly: true },
  { name: 'Relatorios', href: '/relatorios', icon: BarChart3 },
  { name: 'Configuracoes', href: '/configuracoes', icon: Settings },
];

// Menu exclusivo para super_admin
const superAdminNavigation = [
  { name: 'Empresas', href: '/empresas', icon: LayoutDashboard, superAdminOnly: true },
];

const Layout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Combina navegação baseada no role do usuário
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  const filteredNavigation = navigation.filter(item => !item.adminOnly || isAdmin);
  const fullNavigation = user?.role === 'super_admin' 
    ? [...superAdminNavigation, ...filteredNavigation]
    : filteredNavigation;

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="main-layout">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 z-40 bg-slate-900/50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside 
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-slate-900 transform transition-transform duration-300 lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        data-testid="sidebar"
      >
        {/* Logo */}
        <div className="flex items-center justify-between h-16 px-6 border-b border-slate-800">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-blue-600 flex items-center justify-center">
              <span className="text-white font-bold text-sm">R</span>
            </div>
            <div>
              <h1 className="text-white font-semibold text-sm tracking-tight">RPB SISTEMAS</h1>
              <p className="text-slate-400 text-xs">INTELIGENTES</p>
            </div>
          </div>
          <button 
            className="lg:hidden text-slate-400 hover:text-white"
            onClick={() => setSidebarOpen(false)}
          >
            <X size={20} />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {fullNavigation.map((item) => (
            <NavLink
              key={item.name}
              to={item.href}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                  isActive
                    ? 'bg-blue-600/10 text-blue-400 border-l-[3px] border-blue-500 -ml-[3px] pl-[15px]'
                    : 'text-slate-400 hover:text-white hover:bg-slate-800'
                } ${item.superAdminOnly ? 'border border-amber-500/30 bg-amber-500/10' : ''}`
              }
              data-testid={`nav-${item.name.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "")}`}
            >
              <item.icon size={20} />
              {item.name}
              {item.superAdminOnly && (
                <span className="ml-auto text-[10px] bg-amber-500 text-white px-1.5 py-0.5 rounded">ADMIN</span>
              )}
            </NavLink>
          ))}
        </nav>

        {/* User section */}
        <div className="p-4 border-t border-slate-800">
          <div className="flex items-center gap-3 px-3 py-2">
            <div className="w-9 h-9 rounded-full bg-blue-600 flex items-center justify-center">
              <span className="text-white font-semibold text-sm">
                {user?.name?.charAt(0).toUpperCase()}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white text-sm font-medium truncate">{user?.name}</p>
              <p className="text-slate-400 text-xs capitalize">{user?.role}</p>
            </div>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Header */}
        <header className="sticky top-0 z-30 h-16 glass-header border-b border-slate-200" data-testid="header">
          <div className="flex items-center justify-between h-full px-4 lg:px-6">
            {/* Left side */}
            <div className="flex items-center gap-4">
              <button
                className="lg:hidden p-2 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg"
                onClick={() => setSidebarOpen(true)}
                data-testid="mobile-menu-btn"
              >
                <Menu size={20} />
              </button>
              
              {/* Search */}
              <div className="hidden md:flex items-center gap-2 px-3 py-2 bg-slate-100 rounded-lg w-72">
                <Search size={18} className="text-slate-400" />
                <input
                  type="text"
                  placeholder="Buscar..."
                  className="flex-1 bg-transparent border-none text-sm text-slate-600 placeholder:text-slate-400 focus:outline-none"
                  data-testid="search-input"
                />
              </div>
            </div>

            {/* Right side */}
            <div className="flex items-center gap-3">
              {/* Notifications */}
              <button className="relative p-2 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg">
                <Bell size={20} />
                <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
              </button>

              {/* User dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2 px-2" data-testid="user-menu">
                    <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center">
                      <span className="text-white font-semibold text-sm">
                        {user?.name?.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <span className="hidden sm:block text-sm font-medium text-slate-700">{user?.name}</span>
                    <ChevronDown size={16} className="text-slate-400" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-2 py-1.5">
                    <p className="text-sm font-medium">{user?.name}</p>
                    <p className="text-xs text-slate-500">{user?.email}</p>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/configuracoes')}>
                    <Settings size={16} className="mr-2" />
                    Configurações
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="logout-btn">
                    <LogOut size={16} className="mr-2" />
                    Sair
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="p-4 lg:p-6" data-testid="main-content">
          <ErrorBoundary key={location.pathname}>
            <Outlet />
          </ErrorBoundary>
          <GoalCelebration />
        </main>
      </div>
    </div>
  );
};

export default Layout;
